import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSockets } from '../../contexts/WebSocketProvider';
import { useAuth } from '../../contexts/AuthContext';
import { normalizeFollowUp } from '../../services/followUpService';
import { isRecentLocalFollowUpChange, upsertFollowUpInCaches } from '../../lib/state/hooks/followUpCache';
import { followUpActivityLogKeys, followUpConversationKey } from '../../lib/state/hooks/useFollowUpsModuleQueries';
import { useConversationsStore } from '../chat/hooks/FolderUseChat/conversationsStore';
import { showInfo } from '../../utils/toast';
import soundAlertService from '../../services/soundAlertService';
import { SOUND_TYPES } from '../../constants/soundConstants';
import { incrementFollowUpDueBadge, markFollowUpTabUnseen, markFollowUpUnseen } from './utils/followUpDueBadgeStore';
import { getConversationName, getFollowUpTab } from './utils/followUpsModuleUtils';

/**
 * Global (every page) handler for the tenant queue `followup.updated` event:
 *   { event: "followup.updated", action: "CREATE"|"UPDATE"|"STATUS_CHANGE"|"DELETE"|"RESTORE"|"DUE"|"CANCELLED",
 *     follow_up: {...} }
 * - Every action: the follow-up is moved into the right place in all cached lists (Follow-ups page
 *   tabs + Chat Details), so tab counts and lists update without any API call.
 * - Every event not caused by this user: sidebar counter +1, sound, and a green dot on the tab the
 *   follow-up landed in, plus a "new" dot on its card — on every page. DUE also shows a "Follow-up due" toast to its creator.
 *   The counter clears when the Follow-ups page is opened; a tab's dot when that tab is opened.
 */
/**
 * Applies one `followup.updated` event. Exported (pure apart from the cache / badge / toast it
 * updates) so the realtime pipeline is unit-testable.
 * @returns {{ followUp: object|null, notified: boolean }}
 */
/**
 * The action is authoritative for the state it implies, so an incomplete payload still lands the
 * follow-up in the right tab: CANCELLED → cancelled (Trash), DELETE → trashed (Trash),
 * RESTORE → out of the trash. Other actions use the payload as sent.
 */
const applyActionState = (followUp, action) => {
  switch (action) {
    case 'CANCELLED':
      return { ...followUp, status: 'cancelled', is_cancelled: true };
    case 'DELETE':
      return followUp.deleted_at
        ? followUp
        : { ...followUp, is_deleted: true, deleted_at: followUp.updated_at || new Date().toISOString() };
    case 'RESTORE':
      return { ...followUp, is_deleted: false, deleted_at: null };
    default:
      return followUp;
  }
};

export const processFollowUpEvent = (data, { queryClient, currentUserId, handledDueKeys }) => {
  const raw = data?.follow_up || data?.followUp;
  if (!raw || raw.id == null) return { followUp: null, notified: false };
  const action = String(data.action ?? '').toUpperCase();

  // Every action (CREATE / UPDATE / STATUS_CHANGE / DELETE / RESTORE / DUE / CANCELLED): move the
  // follow-up to where it now belongs in every cached list (Due / Upcoming / Completed / Trash —
  // trashed or cancelled go to Trash) → lists and tab counters update without an API call
  const followUp = applyActionState(normalizeFollowUp(raw), action);
  upsertFollowUpInCaches(queryClient, followUp);

  // Its Activity Logs view, if open, gets the new entry: back to page 1, refetched only when shown
  const logsKey = followUpActivityLogKeys.byFollowUp(followUp.id);
  queryClient.setQueryData(logsKey, (logs) =>
    logs?.pages?.length > 1 ? { pages: logs.pages.slice(0, 1), pageParams: logs.pageParams.slice(0, 1) } : logs
  );
  queryClient.invalidateQueries({ queryKey: logsKey, refetchType: 'active' });

  // Re-deliveries of the same event (same follow-up, action and update time) notify only once
  const eventKey = `${followUp.id}|${action}|${action === 'DUE' ? followUp.due_at : followUp.updated_at}`;
  if (handledDueKeys.has(eventKey)) return { followUp, notified: false };
  handledDueKeys.add(eventKey);
  if (handledDueKeys.size > 500) handledDueKeys.delete(handledDueKeys.values().next().value);

  // This user's own change echoed back by the server: lists are updated above, nothing to notify
  if (action !== 'DUE' && isRecentLocalFollowUpChange(followUp.id)) return { followUp, notified: false };

  // Notify on every page: sidebar counter +1, green dot on the tab it landed in (unless that tab is
  // on screen), and a sound (urgent for a follow-up that just became due)
  incrementFollowUpDueBadge();
  markFollowUpTabUnseen(getFollowUpTab(followUp, new Date()));
  markFollowUpUnseen(followUp.id);
  try {
    soundAlertService.playNotificationSound(action === 'DUE' ? SOUND_TYPES.URGENT : SOUND_TYPES.NEW_MESSAGE);
  } catch (error) {
    console.error('Follow-up notification sound failed:', error);
  }

  // "Follow-up due" reminder toast goes to the follow-up's creator (shown if the event doesn't say)
  const creatorId = followUp.created_by?.id;
  const isCreator = creatorId == null || currentUserId == null || Number(creatorId) === Number(currentUserId);
  if (action === 'DUE' && isCreator) {
    const conversation =
      useConversationsStore.getState().byId?.[followUp.conversation_id] ||
      queryClient.getQueryData(followUpConversationKey(followUp.conversation_id));
    const customer = conversation ? getConversationName(conversation) : '';
    showInfo(`Follow-up due: ${followUp.title || 'Follow-up'}${customer ? ` – ${customer}` : ''}`);
  }
  return { followUp, notified: true };
};

const FollowUpRealtimeListener = () => {
  const { tenantWebSocketService } = useWebSockets();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Ref so the socket subscription isn't torn down when the user object changes
  const userIdRef = useRef(user?.id);
  // A follow-up becomes due once per due date — ignore re-deliveries of the same DUE event
  const handledDueRef = useRef(new Set());

  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  useEffect(() => {
    if (!tenantWebSocketService) return undefined;

    const onFollowUpUpdated = (data) =>
      processFollowUpEvent(data, {
        queryClient,
        currentUserId: userIdRef.current,
        handledDueKeys: handledDueRef.current,
      });

    tenantWebSocketService.on('followup.updated', onFollowUpUpdated);
    return () => tenantWebSocketService.off('followup.updated', onFollowUpUpdated);
  }, [tenantWebSocketService, queryClient]);

  return null;
};

export default FollowUpRealtimeListener;
